import React from 'react';
import '../../styles/aims/aims.css';
import projectPlan from '../../assets/img/ProMSAimsimage.webp';
import dnaStrand from '../../assets/svg/dna-solid.svg';
import magnifyingGlass from '../../assets/svg/magnifying-glass.svg';
import floppyDisk from '../../assets/svg/floppy-disk.svg';
import testTube from '../../assets/svg/vial.svg';


const GeneralAim = () => {

	const aims = [
		{
			icon: dnaStrand,
			alt: 'DNA strand',
			text: 'To build a translational environment that integrates proteomics into precision medicine.',
		},
		{
			icon: magnifyingGlass,
			alt: 'Magnifying glass',
			text: 'To harmonize analytical methods and quality control procedures for real-time, single-sample analysis across multiple sites.',
		},
		{
			icon: floppyDisk,
			alt: 'Data storage',
			text: 'To create a national platform for data storage and integrate proteomics data into clinical reporting systems.',
		},
		{
			icon: testTube,
			alt: 'Test tubes',
			text: 'To conduct a pilot study in the form of an observational clinical trial to test the system in a real-world clinical setting.',
		},
	];

	return (
		<section
			id="generalaims"
			className="general-aims"
		>
			<span className="general-aims-body">
				<h2>Aims</h2>
				<p>
					Proteome Medicine brings proteomics into precision diagnostics to improve therapy for each patient. Systematic 
					research in proteomics has led to breakthroughs in analytical technologies, coinciding with advances in computational 
					science. Together, these developments offer remarkable opportunities to comprehensively profile proteomes and quantify 
					large panels of proteins through highly sensitive assays using clinical samples.  
					<br/>
					<br/> 
					The long-standing ambition to bring proteomics to the bedside is becoming a reality. However, successful implementation 
					requires a Swedish Proteome Medicine innovation ecosystem that links cutting-edge academic research with clinical 
					application and industrial development. This will ensure that enabling technologies are made accessible, diagnostic 
					tools are adapted to clinical needs, and translational research is catalyzed to meet the demands of modern healthcare.
					<br/>
					<br/>
					<strong>Our specific aims are:</strong>
					<ul className="aims-list">
						{aims.map(({ icon, alt, text }, idx) => (
						<li key={idx}>
							<img src={icon} alt={alt} className="aims-icon" />
							<span className="aims-text">{text}</span>
						</li>
						))}
					</ul>
				</p>
				<a href={projectPlan} target="_blank" rel="noopener noreferrer">
					<img
						src={projectPlan}
						alt="ProMS Infographic"
						className="projectPlanImg"
					/>
				</a>
				<span className="elaborative-aims">
					<h2>Background</h2>
					<p>
						Sweden is a front-runner in proteomics research, both in mass spectrometry and affinity proteomics, with commercial 
						successes such as Olink. In recent years, Sweden has made significant international-level investments in the BioMS – 
						Swedish National Infrastructure for Biological Mass Spectrometry. BioMS is co-financed by the Swedish Research 
						Council (VR) and host universities: Lund University, Karolinska Institutet, University of Gothenburg, and Chalmers 
						University of Technology. It is also connected to the SciLifeLab proteomics platform. 
						<br/>
						<br/> 
						Since 2005, Karolinska University Hospital has operated the world’s first hospital-based clinical proteomics facility, 
						and since 2022, it also hosts a routine diagnostics-integrated proteomics unit.
						<br/>
						<br/>
						With support from VR, Proteome Medicine Sweden (ProMS) provides an excellent opportunity to harmonize clinical 
						proteomics platforms across Sweden. In 2023, ProMS infrastructure was established at the BioMS nodes in Gothenburg, 
						Lund, and Stockholm. BioMS has also secured continued VR funding to operate from 2025 to 2028.
						<br/>
						<br/>
						Together, these efforts aim to unite key proteomics stakeholders in a national clinical proteomics initiative, 
						strengthening connections with Sweden’s precision medicine ecosystem. This includes Genome Medicine Sweden, 
						Precision Medicine Centers at university hospitals, the Comprehensive Cancer Centers network, healthcare providers, 
						patient organizations, and key industry players such as AstraZeneca (precision medicine), Bruker Daltonics 
						(proteomics technologies), and Beckman Coulter (laboratory automation).  
					</p>
				</span>
			</span>
		</section>
	);
};

export default GeneralAim;
